import toast from 'react-hot-toast';

import {
    handleItemDataLayerPush,
    handleOrderItemClickDatalayerPush,
} from 'Utils/dataLayerPushService';
import { dpr } from 'Utils/dpr';

import { IAlphaMiniCartModel, IOrderItem } from 'Types/Litium';
import CtaButton from 'Components/CtaButton';
import Divider from 'Components/Divider';
import QuantityInputField from 'Components/Forms/QuantityInputField';

import RemoveIcon from 'Icons/closeThin.svg?react';

const CartItemRow = ({
    items,
    isLoading,
    updateCartRow,
    translations,
}: {
    items: IOrderItem[];
    isLoading: boolean;
    updateCartRow: (
        id: string,
        quantity: string,
        type: 'decrement' | 'increment' | 'delete'
    ) => void;
    translations: IAlphaMiniCartModel['translations'];
}) => {
    const renderPrices = (item: IOrderItem) => (
        <li className="flex flex-shrink flex-grow basis-full flex-wrap items-center justify-between">
            {item && item.totalPriceDiscounts?.length ? (
                <>
                    <div className="flex w-full justify-between">
                        <span className="text-base line-through">
                            {translations.cartPriceLabel}:
                        </span>
                        <span className="text-base line-through">
                            {item.unitPrice}
                        </span>
                    </div>
                    {item.totalPriceDiscounts.map((value, i) => (
                        <div
                            className="flex w-full flex-col"
                            key={`${i} - ${item.id}`}
                        >
                            <div className="flex w-full justify-between">
                                <span className="text-persianPink">
                                    {value.discountName}:
                                </span>
                                <span className="text-persianPink">
                                    {value.discountPrice}
                                </span>
                            </div>
                        </div>
                    ))}
                </>
            ) : (
                <div className="flex w-full flex-wrap items-center justify-between">
                    <span className="text-base">
                        {translations.cartPriceLabel}:
                    </span>
                    <span className="text-base">{item.totalPrice}</span>
                </div>
            )}
        </li>
    );

    const handleRemoveItem = (item: IOrderItem) => {
        toast(
            t => (
                <div className="flex flex-col gap-1 font-montserrat">
                    <div className="mr-7 flex gap-1">
                        <p>{translations.removeOrderRowConfirm}</p>
                    </div>
                    <div className="flex">
                        <h4 className="text-lg font-bold">{`${item.name}`}</h4>
                    </div>
                    <div className="flex gap-1">
                        <p>{translations.checkOutItemNumberLabel}:</p>
                        <p className="font-bold">{item.baseId}</p>
                    </div>
                    <div className="flex gap-1">
                        <p>{translations.checkOutColorLabel}:</p>
                        <p className="font-bold">{`${item.color}`}</p>
                    </div>
                    <div className="flex gap-1">
                        <p>{translations.checkOutSizeLabel}:</p>
                        <p className="font-bold">{`${item.size}`}</p>
                    </div>
                    <div className="mt-5 flex gap-2">
                        <CtaButton
                            autoFocus
                            type="button"
                            className="flex basis-1/2 items-center justify-center"
                            ctaType="primary"
                            onClick={() => {
                                updateCartRow(
                                    item.id,
                                    item.itemQuantity.quantity,
                                    'delete'
                                );
                                toast.dismiss(t.id);
                            }}
                        >
                            {translations.confirmRemoveItem}
                        </CtaButton>
                        <CtaButton
                            type="button"
                            className="flex basis-1/2 items-center justify-center"
                            onClick={() => toast.dismiss(t.id)}
                            ctaType="secondary"
                        >
                            {translations.abortRemoveItem}
                        </CtaButton>
                    </div>
                </div>
            ),
            {
                duration: Infinity,
                style: {
                    background: '#ffffff',
                },
            }
        );
    };

    const handleDecrementQuantity = (item: IOrderItem) => {
        if (Number(item.itemQuantity.quantity) === 1) {
            handleRemoveItem(item);
        } else {
            updateCartRow(item.id, item.itemQuantity.quantity, 'decrement');
        }
    };

    return (
        <ul
            className={`transition-opacity duration-layout ${
                isLoading ? 'opacity-50' : 'opacity-100'
            }`}
        >
            {items.map((item, index) => (
                <li
                    className="flex flex-wrap justify-between"
                    key={`${index}-${item.id}`}
                >
                    <div className="flex flex-col min-[550px]:flex-row">
                        <a
                            href={item.url}
                            onClick={() =>
                                handleOrderItemClickDatalayerPush(item)
                            }
                            aria-label="#Go to product"
                        >
                            <figure className="flex flex-shrink flex-grow-0 basis-auto flex-wrap lg:flex-shrink-0 lg:basis-1/3">
                                <picture>
                                    <source
                                        media="(max-width: 1920px)"
                                        srcSet={dpr(
                                            `${item.imageBaseUrl}?width=184&auto=webp`
                                        )}
                                    />
                                    <img
                                        className="h-[140px] w-[140px] object-cover object-bottom min-[550px]:h-[184px] min-[550px]:min-h-[184px] min-[550px]:w-[184px] min-[550px]:min-w-[184px] lg:w-auto "
                                        src={`${item.imageBaseUrl}`}
                                        srcSet={dpr(`${item.imageBaseUrl}`)}
                                        alt={item.name}
                                    />
                                </picture>
                            </figure>
                        </a>
                        <ul className="relative flex h-fit flex-wrap pt-2 min-[550px]:pl-2 min-[550px]:pt-0 lg:flex-grow-0 lg:basis-2/3">
                            <li className="flex flex-grow">
                                <a href={item.url} aria-label="#Go to product">
                                    <h4 className="font-roboto text-base">
                                        {item.name}
                                    </h4>
                                </a>
                            </li>
                            <li className="absolute right-0 top-0 flex">
                                <button
                                    disabled={
                                        !item.itemQuantity.canDecreaseAmount
                                    }
                                    onClick={() => {
                                        handleRemoveItem(item);
                                        handleItemDataLayerPush(item, 'remove');
                                    }}
                                    type="button"
                                >
                                    <span className="sr-only">
                                        {translations.checkOutRemove}
                                    </span>
                                    <RemoveIcon className="h-4 w-4 stroke-2" />
                                </button>
                            </li>
                            <li className="flex flex-shrink flex-grow basis-full flex-wrap items-center justify-between">
                                {translations.checkOutItemNumberLabel}:
                                <span>{item.baseId}</span>
                            </li>
                            <li className="flex flex-shrink flex-grow basis-full flex-wrap items-center justify-between">
                                {translations.checkOutColorLabel}:
                                <span
                                    style={
                                        {
                                            '--color-dot-hex':
                                                item.colorCodeHexValue,
                                        } as React.CSSProperties
                                    }
                                    className={`flex items-center gap-2 after:h-3 after:w-3 after:rounded-full ${
                                        item.colorCodeHexValue
                                            ? '  after:border-[1px] after:border-springWood after:bg-[var(--color-dot-hex)] after:outline after:outline-1 after:outline-black'
                                            : ''
                                    }`}
                                >
                                    {item.color}
                                </span>
                            </li>
                            <li className="flex flex-shrink flex-grow basis-full flex-wrap items-center justify-between">
                                {translations.checkOutSizeLabel}:
                                <span>{item.size}</span>
                            </li>
                            <li className="flex flex-shrink flex-grow basis-full flex-nowrap items-center justify-between text-right">
                                {translations.checkOutStorageProductStatusLabel}:
                                <span
                                    className={`${item.itemQuantity
                                        .showNoMoreInStockMessage
                                        ? 'text-errorRed'
                                        : 'text-black'
                                        }`}
                                    dangerouslySetInnerHTML={{ __html: item.stockStateDescription }}>
                                </span>
                            </li>
                            {renderPrices(item)}
                            <li className="ml-auto">
                                <div className="flex h-10 max-h-10 flex-shrink-0 flex-grow basis-full lg:flex lg:basis-auto">
                                    <QuantityInputField
                                        value={Number(
                                            item.itemQuantity.quantity
                                        )}
                                        minusAction={() => {
                                            handleDecrementQuantity(item);
                                            handleItemDataLayerPush(
                                                item,
                                                'remove'
                                            );
                                        }}
                                        plusAction={() => {
                                            updateCartRow(
                                                item.id,
                                                item.itemQuantity.quantity,
                                                'increment'
                                            );
                                            handleItemDataLayerPush(
                                                item,
                                                'add'
                                            );
                                        }}
                                        plusDisabled={
                                            !item.itemQuantity
                                                .canIncreaseProductAmount
                                        }
                                        minusDisabled={
                                            !item.itemQuantity.canDecreaseAmount
                                        }
                                        translations={{
                                            decrementButton:
                                                translations.checkoutDecrementButton,
                                            incrementButton:
                                                translations.checkoutIncreaseButton,
                                            quantityLabel:
                                                translations.checkOutQuantity,
                                        }}
                                    />
                                </div>
                            </li>
                        </ul>
                    </div>
                    <Divider margin="my-4" />
                </li>
            ))}
        </ul>
    );
};

export default CartItemRow;
